import { createContext, useContext, useEffect, useMemo, type FC, type PropsWithChildren } from 'react';

import type { CmsAdapter, CmsPage, CmsSlotAdder, CmsSource } from '@eneco/cms-adapter';

import type { ComponentsDefinition, WrapComponent } from './types';

/**
 * Builds the value of a `data-sanity` attribute for one document path, or `undefined`
 * where that path cannot be addressed.
 *
 * Typed as an opaque function so this package stays provider-neutral: it never learns
 * what the string means or which CMS produced it, it only places it on a DOM node. The
 * Sanity-aware host supplies one in draft mode (`CmsRouteContainer`), and nothing
 * supplies one otherwise — which is what keeps published markup byte-identical.
 */
export type EditAttribute = (path: string, document?: { id: string; type: string }) => string | undefined;

interface CmsContextValue {
  adapter: CmsAdapter;
  page: CmsPage;
  components: ComponentsDefinition;
  wrapComponent?: WrapComponent;
  editAttribute?: EditAttribute;
  slotAdder?: CmsSlotAdder;
}

const CmsContext = createContext<CmsContextValue | null>(null);

interface CmsProviderProps {
  adapter: CmsAdapter;
  page: CmsPage;
  components: ComponentsDefinition;
  wrapComponent?: WrapComponent;
  /** Set only in draft mode; see {@link EditAttribute}. */
  editAttribute?: EditAttribute;
  /** Set only inside a visual editor, once it can write; see {@link CmsSlotAdder}. */
  slotAdder?: CmsSlotAdder;
}

/**
 * Top-level CMS wiring. Mounted once in `_app.tsx`. Swapping providers means
 * passing a different `adapter` — nothing else changes.
 */
export const CmsProvider: FC<PropsWithChildren<CmsProviderProps>> = ({
  adapter,
  page,
  components,
  children,
  wrapComponent,
  editAttribute,
  slotAdder,
}) => {
  const contextValue = useMemo(
    () => ({ adapter, page, components, wrapComponent, editAttribute, slotAdder }),
    [adapter, page, components, wrapComponent, editAttribute, slotAdder],
  );

  return (
    <CmsContext.Provider value={contextValue}>
      {children}
      <CmsSourceIndicator source={page.context.cmsSource} />
    </CmsContext.Provider>
  );
};

const CmsSourceIndicator: FC<{ source?: CmsSource }> = ({ source }) => {
  useEffect(() => {
    if (source) {
      document.documentElement.dataset.cmsSource = source;
    } else {
      delete document.documentElement.dataset.cmsSource;
    }
    return () => {
      delete document.documentElement.dataset.cmsSource;
    };
  }, [source]);

  return null;
};

export const useCms = (): CmsContextValue => {
  const value = useContext(CmsContext);
  if (!value) throw new Error('useCms must be used inside <CmsProvider>');
  return value;
};
